import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
    BsBattery,
    BsBatteryCharging,
    BsBatteryFull,
    BsBatteryHalf,
} from "react-icons/bs";

import Stat from "../../components/Stat";

function OverlayTriggerContainer({ children, tooltip }) {
    if (!tooltip) {
        return children;
    }

    return (
        <OverlayTrigger
            delay={{ show: 500 }}
            overlay={tooltip}
            popperConfig={{
                modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
            }}
        >
            <span>{children}</span>
        </OverlayTrigger>
    );
}

const formatDuration = (seconds) => {
    if (seconds == null) return "n/a";

    const totalSeconds = Math.floor(seconds); // removes decimals
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    return `${mins}m ${secs}s`;
};

export default function DroneBatteryStat({
    offline,
    batteryInfo,
    batteryPercentageMode,
    batteryVersion,
    remainingFlightDuration,
    oldBatteryPercentage,
    forAdmin,
}) {
    if (!batteryInfo)
        return oldBatteryPercentage ? (
            <Stat
                label="Battery"
                text={
                    <span
                        className={"text-primary"}
                    >{`${oldBatteryPercentage.toFixed()}%`}</span>
                }
                icon={<BsBatteryFull className={"text-primary"} />}
            />
        ) : null;

    const {
        capacity,
        charge,
        current,
        voltage,
        id,
        isCharging,
        percentage,
        timeToFull,
        cycleCount,
    } = batteryInfo;
    const percentageRounded = Math.round(percentage ?? -1);

    const formatValue = (value, unit = "") =>
        value === undefined ? "n/a" : `${value} ${unit}`;

    const isV3 = batteryVersion === "V3";

    const Row = ({ label, value }) => (
        <div
            style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
        >
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );

    const tooltip = forAdmin ? (
        <Tooltip id="battery-tooltip">
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minWidth: 140,
                }}
            >
                <Row label="Voltage" value={formatValue(voltage, "V")} />
                <Row label="Current" value={formatValue(current, "A")} />
                <Row label="Charge" value={formatValue(charge, "Ah")} />
                <Row label="Capacity" value={formatValue(capacity, "Ah")} />

                {isV3 && (
                    <>
                        <Row
                            label="Rem. flight dur."
                            value={formatValue(
                                formatDuration(remainingFlightDuration),
                            )}
                        />
                        <Row
                            label="Time to full"
                            value={
                                timeToFull === 65535
                                    ? "-"
                                    : formatValue(timeToFull, "min")
                            }
                        />
                        <Row
                            label="Cycle count"
                            value={formatValue(cycleCount)}
                        />
                    </>
                )}
                <Row label="ID" value={formatValue(id)} />
                {!isV3 && (
                    <div>
                        Percentage based on{" "}
                        {batteryPercentageMode ? "capacity" : "voltage"}
                    </div>
                )}
            </div>
        </Tooltip>
    ) : null;

    let icon = <BsBatteryFull />;
    let colorClass = "text-success";

    if (percentageRounded >= 35 && percentageRounded <= 100) {
        colorClass = offline ? "text-secondary" : "text-success";
        icon = isCharging ? (
            <BsBatteryCharging
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        ) : (
            <BsBatteryFull
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        );
    } else if (percentageRounded >= 20 && percentageRounded < 35) {
        colorClass = offline ? "text-secondary" : "text-warning";
        icon = isCharging ? (
            <BsBatteryCharging
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        ) : (
            <BsBatteryHalf
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        );
    } else if (percentageRounded >= 0 && percentageRounded < 20) {
        colorClass = offline ? "text-secondary" : "text-danger";
        icon = isCharging ? (
            <BsBatteryCharging
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        ) : (
            <BsBattery className={colorClass} style={{ marginTop: "-2px" }} />
        );
    } else {
        // percentageRounded < 0 || percentageRounded > 100
        colorClass = offline ? "text-secondary" : "text-danger";
        icon = isCharging ? (
            <BsBatteryCharging
                className={colorClass}
                style={{ marginTop: "-2px" }}
            />
        ) : (
            <BsBattery className={colorClass} style={{ marginTop: "-2px" }} />
        );
    }

    return (
        <OverlayTriggerContainer tooltip={tooltip}>
            <Stat
                label="Battery"
                text={
                    <span className={colorClass}>
                        {percentageRounded >= 0 && percentageRounded <= 100
                            ? `${percentageRounded}% `
                            : "Invalid"}
                    </span>
                }
                icon={icon}
            />
        </OverlayTriggerContainer>
    );
}
